/**
 * Pure utility functions for bitwise operations with BigInt
 * These functions are application-agnostic and can be used anywhere
 */

export class BitmapUtils {
  /**
   * Set a bit in the bitmap at the specified index
   * @param bitmap The current bitmap value
   * @param index The bit position to set
   * @returns The new bitmap with the bit set
   */
  static setBit(bitmap: bigint, index: bigint): bigint {
    return bitmap | (1n << index)
  }

  /**
   * Check if a bit is set in the bitmap at the specified index
   * @param bitmap The bitmap to check
   * @param index The bit position to check
   * @returns True if the bit is set, false otherwise
   */
  static hasBit(bitmap: bigint, index: bigint): boolean {
    return (bitmap & (1n << index)) !== 0n
  }

  /**
   * Merge multiple bitmaps using OR operation
   * @param bitmaps Array of bitmaps to merge
   * @returns The merged bitmap
   */
  static mergeBitmaps(...bitmaps: bigint[]): bigint {
    return bitmaps.reduce((acc, bitmap) => acc | bitmap, 0n)
  }

  /**
   * Serialize BigInt to string for JSON storage
   * @param value The BigInt value
   * @returns String representation of the BigInt
   */
  static serializeBigInt(value: bigint): string {
    return value.toString()
  }

  /**
   * Deserialize string back to BigInt
   * @param value The string representation
   * @returns The BigInt value
   */
  static deserializeBigInt(value: string): bigint {
    return BigInt(value)
  }

  /**
   * Convert array of permission keys to bitmap using an enum mapping
   * @param permissions Array of permission keys
   * @param enumObject The enum object mapping keys to BigInt values
   * @returns The bitmap representation
   */
  static permissionsArrayToBitmap(
    permissions: string[],
    enumObject: Record<string, bigint>
  ): bigint {
    let bitmap = 0n
    for (const permission of permissions) {
      const index = enumObject[permission]
      if (index !== undefined) {
        bitmap = this.setBit(bitmap, index)
      }
    }
    return bitmap
  }

  /**
   * Check if all bits from required bitmap are set in the actual bitmap
   * @param actualBitmap The bitmap to check
   * @param requiredBitmap The required bits
   * @returns True if all required bits are set
   */
  static hasAllBits(actualBitmap: bigint, requiredBitmap: bigint): boolean {
    return (actualBitmap & requiredBitmap) === requiredBitmap
  }

  /**
   * Check if any bits from required bitmap are set in the actual bitmap
   * @param actualBitmap The bitmap to check
   * @param requiredBitmap The required bits
   * @returns True if any required bits are set
   */
  static hasAnyBits(actualBitmap: bigint, requiredBitmap: bigint): boolean {
    return (actualBitmap & requiredBitmap) !== 0n
  }

  /**
   * Clear a bit in the bitmap at the specified index
   * @param bitmap The current bitmap value
   * @param index The bit position to clear
   * @returns The new bitmap with the bit cleared
   */
  static clearBit(bitmap: bigint, index: bigint): bigint {
    return bitmap & ~(1n << index)
  }

  /**
   * Toggle a bit in the bitmap at the specified index
   * @param bitmap The current bitmap value
   * @param index The bit position to toggle
   * @returns The new bitmap with the bit toggled
   */
  static toggleBit(bitmap: bigint, index: bigint): bigint {
    return bitmap ^ (1n << index)
  }

  /**
   * Count the number of set bits in a bitmap
   * @param bitmap The bitmap to count
   * @returns The number of set bits
   */
  static countBits(bitmap: bigint): number {
    let count = 0
    let n = bitmap
    while (n > 0n) {
      count++
      n = n & (n - 1n) // Clear the least significant set bit
    }
    return count
  }

  /**
   * Get all set bit positions in a bitmap
   * @param bitmap The bitmap to analyze
   * @returns Array of bit positions that are set
   */
  static getSetBitPositions(bitmap: bigint): bigint[] {
    const positions: bigint[] = []
    let position = 0n
    let n = bitmap

    while (n > 0n) {
      if (n & 1n) {
        positions.push(position)
      }
      n = n >> 1n
      position++
    }

    return positions
  }
}
